import { useState } from "react";
import { Input } from "antd";
import {
    getEditableTranslatableValue,
    LANGUAGE_CODE_LABEL,
    TRANSLATABLE_PLACEHOLDER_BY_LANGUAGE,
} from "./translatable.util";
import { THANK_YOU_PAYMENT_METHODS } from "./thankYouLayouts.constants";
import { getConfig } from "../../../config";

// Fixed 4-row editor for `type: "paymentMethodTexts"` schema fields —
// visually modeled on CardsEditor's expand/collapse UX, but with no add/
// remove controls: payment methods are a fixed constant (Card/Transfer/
// Cash/Tabby), not an admin-managed list. Each row falls back to an empty
// string for its own instructions if the corresponding method key is
// absent from `value` — a row is never missing or extra even against an
// incomplete saved object (componentRegistry.js's mergeEntry backfill
// already tops up a missing method key on load/publish; this editor's own
// per-row fallback is just defensive, not the only thing preventing a gap).
// Tabby is a MEELZ-only payment method (UAE "buy now pay later" provider) —
// its instruction row is hidden for every other brand's admin, matching the
// client's own NEXT_PUBLIC_ENABLE_TABBY gate on the checkout side. Without
// this, a CK/E4F/DEMO admin could fill in Tabby instructions for a payment
// method their brand's checkout never offers.
const VISIBLE_PAYMENT_METHODS = getConfig().projectCode === "MEELZ"
    ? THANK_YOU_PAYMENT_METHODS
    : THANK_YOU_PAYMENT_METHODS.filter((method) => method !== "Tabby");

function PaymentMethodTextsEditor({ value, onChange, selectedLanguage = "en" }) {
    const [expanded, setExpanded] = useState(null);

    const updateMethodInstructions = (method, val) => {
        const existing = value?.[method]?.instructions;
        const asObject = existing == null || typeof existing === "string"
            ? (existing ? { en: existing } : {})
            : existing;
        onChange({
            ...value,
            [method]: {
                ...value?.[method],
                instructions: { ...asObject, [selectedLanguage]: val },
            },
        });
    };

    return (
        <div style={{ width: "100%", padding: "0 16px 8px" }}>
            {VISIBLE_PAYMENT_METHODS.map((method) => (
                <div key={method} style={{ border: "1px solid #e5e7eb", borderRadius: 6, marginBottom: 6, overflow: expanded === method ? "visible" : "hidden" }}>
                    <div
                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "6px 10px", background: "#fafafa", cursor: "pointer" }}
                        onClick={() => setExpanded(expanded === method ? null : method)}
                    >
                        <span style={{ fontSize: 12, color: "#444" }}>{method}</span>
                        <span style={{ fontSize: 10, color: "#bbb" }}>{expanded === method ? "▲" : "▼"}</span>
                    </div>
                    {expanded === method && (
                        <div style={{ padding: "8px 10px", display: "flex", flexDirection: "column", gap: 5 }}>
                            <span style={{ fontSize: 11, color: "#888" }}>
                                Instructions ({LANGUAGE_CODE_LABEL[selectedLanguage]})
                            </span>
                            <Input.TextArea
                                size="small"
                                autoSize={{ minRows: 2, maxRows: 6 }}
                                value={getEditableTranslatableValue(value?.[method]?.instructions, selectedLanguage)}
                                onChange={(e) => updateMethodInstructions(method, e.target.value)}
                                placeholder={TRANSLATABLE_PLACEHOLDER_BY_LANGUAGE[selectedLanguage]("instructions")}
                                dir={selectedLanguage === "ar" ? "rtl" : "ltr"}
                            />
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
}

export { PaymentMethodTextsEditor };
